import { create } from 'zustand'

export interface UploadedFile {
  id: string
  name: string
  type: 'pdf' | 'image'
  size: number
  pdfText?: string // Extracted text for PDFs (for Grok/Claude)
  pdfBase64?: string // Raw PDF bytes as base64 (for Gemini native vision)
  pageImages?: string[] // Rendered page images as base64 JPEG (for vision fallback)
  imageBase64?: string // Base64 data for images
}

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface ProcessorState {
  // File state
  uploadedFile: UploadedFile | null
  isUploading: boolean
  uploadError: string | null

  // Model selection
  selectedModel: string

  // Chat state
  messages: Message[]
  isProcessing: boolean
  processingError: string | null

  // Actions
  setUploadedFile: (file: UploadedFile | null) => void
  setIsUploading: (uploading: boolean) => void
  setUploadError: (error: string | null) => void
  setSelectedModel: (model: string) => void
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void
  updateLastAssistantMessage: (content: string) => void
  setIsProcessing: (processing: boolean) => void
  setProcessingError: (error: string | null) => void
  clearConversation: () => void
  reset: () => void
}

export const useProcessorStore = create<ProcessorState>((set) => ({
  // Initial state
  uploadedFile: null,
  isUploading: false,
  uploadError: null,
  selectedModel: 'grok-2-vision-1212', // Default to best vision model
  messages: [],
  isProcessing: false,
  processingError: null,

  // Actions
  setUploadedFile: (file) => set({ uploadedFile: file, uploadError: null }),
  setIsUploading: (uploading) => set({ isUploading: uploading }),
  setUploadError: (error) => set({ uploadError: error, isUploading: false }),
  setSelectedModel: (model) => set({ selectedModel: model }),

  addMessage: (message) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          ...message,
          id: crypto.randomUUID(),
          timestamp: new Date(),
        },
      ],
    })),

  updateLastAssistantMessage: (content) =>
    set((state) => {
      const messages = [...state.messages]
      const lastIdx = messages.length - 1
      if (lastIdx >= 0 && messages[lastIdx].role === 'assistant') {
        messages[lastIdx] = { ...messages[lastIdx], content }
      }
      return { messages }
    }),

  setIsProcessing: (processing) => set({ isProcessing: processing }),
  setProcessingError: (error) => set({ processingError: error, isProcessing: false }),

  clearConversation: () => set({ messages: [], processingError: null }),

  reset: () =>
    set({
      uploadedFile: null,
      isUploading: false,
      uploadError: null,
      selectedModel: 'grok-2-vision-1212',
      messages: [],
      isProcessing: false,
      processingError: null,
    }),
}))
